import { useQuery } from '@tanstack/react-query';

import { ApiError } from 'shared/errors/ApiError';

import { fbmOrdersV3 } from '~/api/FBM/fbmOrdersV3';

/**
 * Query keys for the FBM order deep dive query
 * @typedef {Object} FbmOrderDeepDiveQueryKeys
 * @property {string[]} all
 * @property {string[]} byOrderNum
 * @property {string[]} byOrderNumAndFbmAccountName
 */
export const fbmOrderDeepDiveQueryKeys = {
    all: ['fbmOrderDeepDive'],
    byOrderNum: (orderNum) => ['fbmOrderDeepDive', orderNum],
    byOrderNumAndFbmAccountName: (orderNum, fbmAccountName) => ['fbmOrderDeepDive', orderNum, fbmAccountName],
};

/**
 *
 * @param {string} orderNum
 * @param {string} fbmAccountName
 * @returns {import('@tanstack/react-query').UseQueryResult<Object, ApiError>}
 */
export const useGetOrderDeepDiveQuery = (orderNum, fbmAccountName) =>
    useQuery({
        enabled: !!orderNum && !!fbmAccountName,
        queryFn: async () => {
            const response = await fbmOrdersV3.getDeepDive(orderNum, fbmAccountName);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch FBM order deep dive',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data.data;
        },
        queryKey: fbmOrderDeepDiveQueryKeys.byOrderNumAndFbmAccountName(orderNum, fbmAccountName),
        retry: (failureCount, error) => {
            // Don't retry on server errors (5xx) - they won't resolve themselves
            if (error instanceof ApiError && error.isServerError) {
                return false;
            }
            // Don't retry on auth errors
            if (error instanceof ApiError && [401, 403].includes(error.statusCode)) {
                return false;
            }
            // Retry up to 2 times for other errors (network issues, 4xx that might be transient)
            return failureCount < 2;
        },
    });
